"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getCaptchaToken } from "@/lib/captcha";

export type CaptchaStatus = "solving" | "ready" | "error";

/**
 * Solves a proof-of-work captcha in the background on mount, so a token is
 * usually ready by the time the user submits. `ensureToken()` awaits the
 * in-flight solve (or starts one) and returns a token; `reset()` arms a fresh
 * one after a successful submit (tokens are single-use / short-lived).
 * `status` drives the visible widget (solving / ready / error).
 */
export function useCaptcha() {
  const [status, setStatus] = useState<CaptchaStatus>("solving");
  const tokenRef = useRef<string | null>(null);
  const promiseRef = useRef<Promise<string> | null>(null);

  const solve = useCallback((): Promise<string> => {
    setStatus("solving");
    const p = getCaptchaToken()
      .then((t) => {
        tokenRef.current = t;
        setStatus("ready");
        return t;
      })
      .catch((e) => {
        promiseRef.current = null;
        setStatus("error");
        throw e;
      });
    promiseRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    solve().catch(() => {
      /* surfaced via status="error"; retried lazily in ensureToken / onRetry */
    });
  }, [solve]);

  const ensureToken = useCallback((): Promise<string> => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current);
    return promiseRef.current ?? solve();
  }, [solve]);

  const reset = useCallback(() => {
    tokenRef.current = null;
    solve().catch(() => {});
  }, [solve]);

  return { status, ensureToken, reset };
}
